import {
  add,
  angleDelta,
  angleOf,
  clamp,
  distance,
  distanceSquared,
  fromAngle,
  scale,
  subtract,
  vec,
  type Vec2,
} from "./math";
import { SeededRng } from "./rng";
import type {
  BotVisibleSnake,
  BotWorldView,
  Food,
  SnakeIntent,
  SnakeState,
} from "./types";

const cloneSnake = (snake: SnakeState): SnakeState => ({
  ...snake,
  position: { ...snake.position },
  trail: { points: snake.trail.points.map((point) => ({ ...point })) },
});

const cloneFood = (food: Food): Food => ({
  ...food,
  position: { ...food.position },
});

const visibleSnakeProjection = (
  snake: SnakeState,
  observer: SnakeState,
  perceptionRadius: number,
): BotVisibleSnake => ({
  id: snake.id,
  position: { ...snake.position },
  heading: snake.heading,
  length: snake.length,
  width: snake.width,
  speed: snake.speed,
  protectionRemainingSeconds: snake.protectionRemainingSeconds,
  bodyPoints: snake.trail.points
    .filter(
      (point) =>
        distanceSquared(observer.position, point) <= perceptionRadius ** 2,
    )
    .map((point) => ({ ...point })),
});

export const visibleWorldForBot = (
  self: SnakeState,
  snakes: readonly SnakeState[],
  foods: readonly Food[],
  elapsedSeconds: number,
  perceptionRadius: number,
  arenaRadius: number,
): BotWorldView => {
  const withinRange = (position: { x: number; y: number }): boolean =>
    distanceSquared(self.position, position) <=
    perceptionRadius * perceptionRadius;
  return {
    elapsedSeconds,
    self: cloneSnake(self),
    visibleSnakes: snakes
      .filter(
        (snake) =>
          snake.id !== self.id &&
          snake.alive &&
          (withinRange(snake.position) || snake.trail.points.some(withinRange)),
      )
      .sort((left, right) => left.id.localeCompare(right.id))
      .map((snake) => visibleSnakeProjection(snake, self, perceptionRadius)),
    visibleFoods: foods
      .filter((food) => withinRange(food.position))
      .sort((left, right) => left.id.localeCompare(right.id))
      .map(cloneFood),
    arenaRadius,
  };
};

export class BotController {
  private readonly random: SeededRng;
  private nextDecisionAt = Number.NEGATIVE_INFINITY;
  private lastIntent: SnakeIntent = { targetHeading: 0, boosting: false };
  private desiredIntent: SnakeIntent = { targetHeading: 0, boosting: false };
  private lastSteeringAt = Number.NEGATIVE_INFINITY;
  private target: { readonly id: string; readonly lockedAt: number } | null =
    null;
  private movementHistory: Array<{
    readonly at: number;
    readonly position: Vec2;
    readonly heading: number;
  }> = [];
  private progressHistory: Array<{
    readonly at: number;
    readonly position: Vec2;
    readonly heading: number;
  }> = [];
  private recoveryUntil = Number.NEGATIVE_INFINITY;
  private recoveryCooldownUntil = Number.NEGATIVE_INFINITY;
  private recoveryTarget: Vec2 | null = null;
  private displacementWindowIndex = Number.NEGATIVE_INFINITY;
  private displacementWindowOrigin: Vec2 | null = null;
  private displacementGuardHeading: number | null = null;
  private displacementGuardUntil = Number.NEGATIVE_INFINITY;
  private ignoredTarget: {
    readonly id: string;
    readonly until: number;
  } | null = null;

  constructor(
    readonly snakeId: string,
    seed: string | number,
    readonly reactionSeconds: number,
  ) {
    this.random = new SeededRng(`${String(seed)}:${snakeId}`);
  }

  get debugState(): {
    readonly recovering: boolean;
    readonly targetId: string | null;
    readonly historySamples: number;
  } {
    const latest = this.movementHistory.at(-1)?.at ?? Number.NEGATIVE_INFINITY;
    return {
      recovering: latest < this.recoveryUntil,
      targetId: this.target?.id ?? null,
      historySamples: this.movementHistory.length,
    };
  }

  reset(): void {
    this.nextDecisionAt = Number.NEGATIVE_INFINITY;
    this.lastIntent = { targetHeading: 0, boosting: false };
    this.desiredIntent = { targetHeading: 0, boosting: false };
    this.lastSteeringAt = Number.NEGATIVE_INFINITY;
    this.target = null;
    this.movementHistory = [];
    this.progressHistory = [];
    this.recoveryUntil = Number.NEGATIVE_INFINITY;
    this.recoveryCooldownUntil = Number.NEGATIVE_INFINITY;
    this.recoveryTarget = null;
    this.displacementWindowIndex = Number.NEGATIVE_INFINITY;
    this.displacementWindowOrigin = null;
    this.displacementGuardHeading = null;
    this.displacementGuardUntil = Number.NEGATIVE_INFINITY;
    this.ignoredTarget = null;
  }

  private observeMovement(view: BotWorldView): void {
    const displacementWindowIndex = Math.floor(view.elapsedSeconds / 15);
    if (displacementWindowIndex !== this.displacementWindowIndex) {
      this.displacementWindowIndex = displacementWindowIndex;
      this.displacementWindowOrigin = { ...view.self.position };
      this.displacementGuardHeading = null;
      this.displacementGuardUntil = Number.NEGATIVE_INFINITY;
    }
    const displacementWindowPhase =
      view.elapsedSeconds - displacementWindowIndex * 15;
    if (
      displacementWindowPhase >= 9.5 &&
      this.displacementGuardHeading === null &&
      this.displacementWindowOrigin
    ) {
      const away = subtract(view.self.position, this.displacementWindowOrigin);
      this.displacementGuardHeading =
        distanceSquared(away, vec(0, 0)) < 1
          ? view.self.heading
          : angleOf(away);
      this.displacementGuardUntil = (displacementWindowIndex + 1) * 15;
    }
    this.movementHistory.push({
      at: view.elapsedSeconds,
      position: { ...view.self.position },
      heading: view.self.heading,
    });
    this.progressHistory.push({
      at: view.elapsedSeconds,
      position: { ...view.self.position },
      heading: view.self.heading,
    });
    const cutoff = view.elapsedSeconds - 4;
    while ((this.movementHistory[0]?.at ?? cutoff) < cutoff)
      this.movementHistory.shift();
    const progressCutoff = view.elapsedSeconds - 15;
    while ((this.progressHistory[0]?.at ?? progressCutoff) < progressCutoff)
      this.progressHistory.shift();

    const oldest = this.movementHistory[0];
    const newest = this.movementHistory.at(-1);
    if (!oldest || !newest) return;
    const progressOldest = this.progressHistory[0];
    let longWindowTurn = 0;
    for (let index = 1; index < this.progressHistory.length; index += 1) {
      const previous = this.progressHistory[index - 1];
      const current = this.progressHistory[index];
      if (previous && current)
        longWindowTurn += Math.abs(
          angleDelta(previous.heading, current.heading),
        );
    }
    const progressAge =
      view.elapsedSeconds - (progressOldest?.at ?? view.elapsedSeconds);
    const progressDistance = distance(
      progressOldest?.position ?? newest.position,
      newest.position,
    );
    const lowLongTermProgress =
      Boolean(progressOldest) &&
      ((progressAge >= 6 && progressDistance < 180) ||
        (progressAge >= 6 &&
          progressDistance < 1_200 &&
          longWindowTurn > Math.PI * 0.5));
    if (
      (newest.at - oldest.at < 3.8 && !lowLongTermProgress) ||
      (view.self.protectionRemainingSeconds ?? 0) > 0 ||
      view.elapsedSeconds < this.recoveryUntil
    ) {
      return;
    }
    let cumulativeTurn = 0;
    for (let index = 1; index < this.movementHistory.length; index += 1) {
      const previous = this.movementHistory[index - 1];
      const current = this.movementHistory[index];
      if (previous && current)
        cumulativeTurn += Math.abs(
          angleDelta(previous.heading, current.heading),
        );
    }
    if (
      !lowLongTermProgress &&
      (cumulativeTurn <= Math.PI * 2 ||
        distance(oldest.position, newest.position) >= 80)
    ) {
      return;
    }

    const center = this.movementHistory.reduce(
      (sum, sample) => add(sum, sample.position),
      vec(0, 0),
    );
    const average = scale(center, 1 / this.movementHistory.length);
    let escapeDirection = subtract(view.self.position, average);
    if (distanceSquared(escapeDirection, vec(0, 0)) < 1)
      escapeDirection = fromAngle(view.self.heading + Math.PI / 2);
    const escapeHeading = angleOf(escapeDirection);
    const maximumRadius = view.arenaRadius - 180;
    const candidate = add(
      view.self.position,
      scale(fromAngle(escapeHeading), 320),
    );
    const candidateDistance = distance(candidate, vec(0, 0));
    this.recoveryTarget =
      candidateDistance > maximumRadius
        ? scale(candidate, maximumRadius / Math.max(1, candidateDistance))
        : candidate;
    this.recoveryUntil = view.elapsedSeconds + 1.5;
    this.recoveryCooldownUntil = view.elapsedSeconds + 7.5;
    if (this.target) {
      this.ignoredTarget = {
        id: this.target.id,
        until: view.elapsedSeconds + 6,
      };
    }
    this.target = null;
    this.movementHistory = [newest];
    this.progressHistory = [
      {
        at: newest.at,
        position: { ...newest.position },
        heading: newest.heading,
      },
    ];
  }

  private dangerHeading(view: BotWorldView): number | null {
    const self = view.self;
    const projected = add(
      self.position,
      scale(fromAngle(self.heading), self.speed * 0.6),
    );
    let nearestDanger: Vec2 | null = null;
    let nearestDistanceSquared = Number.POSITIVE_INFINITY;
    for (const snake of view.visibleSnakes) {
      const headProjected = add(
        snake.position,
        scale(fromAngle(snake.heading), (snake.speed ?? self.speed) * 0.35),
      );
      for (const point of [headProjected, ...snake.bodyPoints]) {
        const candidateDistance = distanceSquared(projected, point);
        if (candidateDistance < nearestDistanceSquared) {
          nearestDanger = point;
          nearestDistanceSquared = candidateDistance;
        }
      }
    }
    if (nearestDanger && nearestDistanceSquared < 95 ** 2)
      return angleOf(subtract(self.position, nearestDanger));

    const projectedDistance = distance(projected, vec(0, 0));
    if (projectedDistance > view.arenaRadius - 140) {
      const inward = angleOf(scale(self.position, -1));
      const side =
        [...this.snakeId].reduce(
          (sum, character) => sum + character.charCodeAt(0),
          0,
        ) %
          2 ===
        0
          ? 1
          : -1;
      return inward + side * 0.42;
    }
    return null;
  }

  private foodTarget(view: BotWorldView): Food | null {
    const score = (food: Food): number =>
      (food.baseValue * (food.source === "elimination" ? 2.25 : 1)) /
      Math.max(1, distanceSquared(view.self.position, food.position));
    if (
      this.ignoredTarget &&
      this.ignoredTarget.until <= view.elapsedSeconds + 1e-9
    ) {
      this.ignoredTarget = null;
    }
    const availableFoods = view.visibleFoods.filter(
      (food) => food.id !== this.ignoredTarget?.id,
    );
    const byUtility = [...availableFoods].sort(
      (left, right) =>
        score(right) - score(left) || left.id.localeCompare(right.id),
    );
    const best = byUtility[0] ?? null;
    const current = this.target
      ? (availableFoods.find((food) => food.id === this.target?.id) ?? null)
      : null;
    if (!current) {
      this.target = best
        ? { id: best.id, lockedAt: view.elapsedSeconds }
        : null;
      return best;
    }
    if (
      best &&
      best.id !== current.id &&
      view.elapsedSeconds - (this.target?.lockedAt ?? 0) >= 0.8 &&
      score(best) >= score(current) * 1.15
    ) {
      this.target = { id: best.id, lockedAt: view.elapsedSeconds };
      return best;
    }
    return current;
  }

  decide(view: BotWorldView): SnakeIntent {
    if (view.elapsedSeconds + 1e-9 < this.nextDecisionAt) {
      return this.incrementalIntent(view);
    }
    this.nextDecisionAt = view.elapsedSeconds + this.reactionSeconds;
    this.observeMovement(view);

    const self = view.self;
    let targetHeading: number;

    const dangerHeading = this.dangerHeading(view);
    const threatened = dangerHeading !== null;
    if (dangerHeading !== null) {
      targetHeading = dangerHeading;
    } else if (
      this.displacementGuardHeading !== null &&
      view.elapsedSeconds < this.displacementGuardUntil
    ) {
      targetHeading = this.displacementGuardHeading;
    } else if (
      view.elapsedSeconds < this.recoveryUntil &&
      this.recoveryTarget
    ) {
      targetHeading = angleOf(subtract(this.recoveryTarget, self.position));
    } else if (view.elapsedSeconds < this.recoveryCooldownUntil) {
      targetHeading = self.heading;
    } else {
      const targetFood = this.foodTarget(view);
      targetHeading = targetFood
        ? angleOf(subtract(targetFood.position, self.position))
        : self.heading + this.random.between(-0.55, 0.55);
    }

    this.desiredIntent = {
      targetHeading,
      boosting:
        !threatened &&
        self.length > 130 &&
        view.visibleFoods.some(
          (food) =>
            food.source === "elimination" &&
            distance(self.position, food.position) < 240,
        ),
    };
    return this.incrementalIntent(view);
  }

  private incrementalIntent(view: BotWorldView): SnakeIntent {
    const elapsedSinceSteering = Number.isFinite(this.lastSteeringAt)
      ? Math.max(0, view.elapsedSeconds - this.lastSteeringAt)
      : 1 / 60;
    this.lastSteeringAt = view.elapsedSeconds;
    const tacticalTurnRate =
      3.5 * clamp(Math.sqrt(160 / Math.max(1, view.self.length)), 0.75, 1);
    const maximumTurn = tacticalTurnRate * elapsedSinceSteering;
    this.lastIntent = {
      targetHeading:
        view.self.heading +
        clamp(
          angleDelta(view.self.heading, this.desiredIntent.targetHeading),
          -maximumTurn,
          maximumTurn,
        ),
      boosting: this.desiredIntent.boosting,
    };
    return this.lastIntent;
  }
}
